import { z } from "zod";
import { CREDENTIAL_TARGET_TYPES, type CredentialTargetType } from "./credentialTargets";
import { CONNECTOR_CAUSE_CODES } from "./notifications";

export { CREDENTIAL_TARGET_TYPES, type CredentialTargetType };

/**
 * Credential checks (2026-09-28 design "catch expiring and rejected credentials before the
 * nightly import", with the owner's answers of 2026-09-28).
 *
 * The runner re-checks every Ready connection and every bound destination credential once a day
 * (`CREDENTIAL_CHECK_AT`, default 23:00 America/Los_Angeles) and records the result in the
 * platform table `credential_checks`, never in `dataflows.last_validation`: a failure there would
 * flip a Ready connection to draft and the scheduler would silently stop importing it. Expiry
 * dates live in `credential_expiries`, one row per credential field with a known date.
 *
 * SEC-17: every view below carries identifiers, closed codes, labels and dates. Never a value,
 * never a secret name, never the hub's own message.
 */

/**
 * `not_checked` before the first check; `valid` accepted; `rejected` the vendor said no;
 * `unreachable` the check could not ask (hub down, timeout, DNS, a source that is off at check
 * time). Unreachable is NOT a credential failure and never alerts the customer on its own.
 */
export const CREDENTIAL_CHECK_STATUSES = [
  "not_checked",
  "valid",
  "rejected",
  "unreachable",
] as const;
export type CredentialCheckStatus = (typeof CREDENTIAL_CHECK_STATUSES)[number];

/** Where an expiry date came from: the token itself (`se`), the provider (GitHub), or a person. */
export const CREDENTIAL_EXPIRY_SOURCES = ["token", "provider", "entered"] as const;

/** The deployment default for the daily check (owner answer a): 11 PM Pacific. */
export const DEFAULT_CREDENTIAL_CHECK_AT = "23:00 America/Los_Angeles";

/** The retry before a scheduled import, when the daily check did not reach the source. */
export const CREDENTIAL_PRE_IMPORT_MINUTES = 30;

/** The feature flag in `features.flags`. Absent means on; `false` stops the checks. */
export const CREDENTIAL_CHECK_FLAG = "credentials.check";

/** The Overview warns when the runner's last heartbeat is older than this (owner answer f). */
export const RUNNER_HEARTBEAT_STALE_MINUTES = 10;

/** The destination sections that are checked, with the words the screens use. */
export const CREDENTIAL_TARGET_LABELS = {
  dataflow: "Connection",
  stage: "Stage storage",
  warehouse: "Warehouse sign-in",
  dbt_repo: "dbt repository",
} as const satisfies Record<CredentialTargetType, string>;

/**
 * The binding fields a person may give an expiry date, by section (a key pair and a git token
 * carry none of their own). Databricks waits for its loader (owner answer d). The stage SAS
 * (`adlsKey`) is retired (#399); `stage` stays a target type so an older row or event still reads.
 */
export const BINDING_EXPIRY_FIELDS = ["snowflakePrivateKey", "dbtGitSecret"] as const;
export type BindingExpiryField = (typeof BINDING_EXPIRY_FIELDS)[number];

export const BINDING_EXPIRY_SECTION = {
  snowflakePrivateKey: "warehouse",
  dbtGitSecret: "dbt_repo",
} as const satisfies Record<BindingExpiryField, Exclude<CredentialTargetType, "dataflow">>;

export const BINDING_EXPIRY_FIELD_LABELS = {
  snowflakePrivateKey: "Key pair",
  dbtGitSecret: "Access token",
} as const satisfies Record<BindingExpiryField, string>;

const iso = z.string();

/** A `YYYY-MM-DD` day, read as midnight UTC. */
export const expiryDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date such as 2027-03-27.")
  .refine((value) => Number.isFinite(Date.parse(value)), "Use a real date.");

export const credentialExpiryViewSchema = z.object({
  /** A connection field id, or a binding field name (`snowflakePrivateKey`). */
  field: z.string(),
  /** The field's label from the connector definition, or the binding's words. */
  label: z.string(),
  expiresAt: iso,
  source: z.enum(CREDENTIAL_EXPIRY_SOURCES),
  daysLeft: z.number().int(),
});
export type CredentialExpiryView = z.infer<typeof credentialExpiryViewSchema>;

export const credentialTargetViewSchema = z.object({
  targetType: z.enum(CREDENTIAL_TARGET_TYPES),
  /** The connection id, or the instance id for a destination section. */
  targetId: z.string(),
  /** The connection's name, or the section's words. */
  name: z.string(),
  /** The connection's connector (#1097: a SQL connector's refusal names more fields); null for a section. */
  connectorName: z.string().nullable(),
  status: z.enum(CREDENTIAL_CHECK_STATUSES),
  causeCode: z.enum(CONNECTOR_CAUSE_CODES).nullable(),
  /** The last check, whatever it found. */
  checkedAt: iso.nullable(),
  /** The last check that got an answer from the source (accepted or refused). */
  reachedAt: iso.nullable(),
  lastValidAt: iso.nullable(),
  failingSince: iso.nullable(),
  nextCheckAt: iso.nullable(),
  expiries: z.array(credentialExpiryViewSchema),
});
export type CredentialTargetView = z.infer<typeof credentialTargetViewSchema>;

/** `GET /api/v2/instances/current/credentials`. */
export const credentialsViewSchema = z.object({
  /** False when `features.flags["credentials.check"]` is false: the screens hide the check lines. */
  checksEnabled: z.boolean(),
  /**
   * Every connection's row, and the destination's rows (stage, warehouse sign-in, dbt repository)
   * only for a holder of `instance.binding.read` (#1306, Instance admin and above).
   */
  targets: z.array(credentialTargetViewSchema),
  /** #1306: present (true) when the destination's rows were left out for this reader. */
  destinationWithheld: z.literal(true).optional(),
});
export type CredentialsView = z.infer<typeof credentialsViewSchema>;

/** `PUT /api/v2/dataflows/:id/credential-expiry`: a date for one expiring credential field. */
export const putDataflowCredentialExpiryBodySchema = z
  .object({
    field: z.string().min(1).max(100),
    /** Null clears the date. */
    expiresOn: expiryDaySchema.nullable(),
  })
  .strict();
export type PutDataflowCredentialExpiryBody = z.infer<typeof putDataflowCredentialExpiryBodySchema>;

/** `PUT /api/v2/instances/current/binding/credential-expiry`. */
export const putBindingCredentialExpiryBodySchema = z
  .object({
    field: z.enum(BINDING_EXPIRY_FIELDS),
    expiresOn: expiryDaySchema.nullable(),
  })
  .strict();
export type PutBindingCredentialExpiryBody = z.infer<typeof putBindingCredentialExpiryBodySchema>;

/** What both expiry routes answer: the field's row after the write, or null when cleared. */
export const credentialExpiryResultSchema = z.object({
  field: z.string(),
  expiry: credentialExpiryViewSchema.nullable(),
});
export type CredentialExpiryResult = z.infer<typeof credentialExpiryResultSchema>;
